import { defineTokens } from '@chakra-ui/react'

/**
 * Raw token values — the design's vocabulary, before anything is given a role.
 *
 * Nothing outside `src/theme` should reference these directly. Components use
 * the semantic tokens in `./semantic` (`bg`, `fg`, `border`, `colorPalette`),
 * which is what keeps a dark palette to a one-file change later. See
 * openspec/changes/establish-design-system/design.md.
 *
 * Values marked ANCHOR carry the identity and were taken from the reference
 * design. The steps between them are interpolated and have not been checked
 * for even perceptual spacing — treat a middle step as provisional.
 */
export const tokens = defineTokens({
  colors: {
    /** The brand green. Ink, sidebar, and the `forest` colour palette. */
    forest: {
      50: { value: '#EFF4F0' },
      // ANCHOR — buyer's message bubble
      100: { value: '#DEE4DC' },
      200: { value: '#C6D3C8' },
      300: { value: '#A6B8AB' },
      400: { value: '#829A8C' },
      500: { value: '#5E7A6C' },
      600: { value: '#3F6154' },
      700: { value: '#345247' },
      // ANCHOR — active nav item
      800: { value: '#274840' },
      // ANCHOR — sidebar ground, and the ink
      900: { value: '#1C322E' },
      950: { value: '#14241F' },
    },

    /**
     * Deliberately NOT grey.
     *
     * This overrides Chakra's own `gray` scale rather than sitting beside it as
     * a differently-named palette. Chakra's semantic tokens (`bg.subtle`,
     * `bg.muted`, `border`, and every component recipe that reads them) resolve
     * through `gray`, so replacing the scale is what moves the whole library at
     * once. A palette added alongside would leave everything we forgot to dress
     * sitting cool-blue on a warm page.
     *
     * The name is a lie we accept in exchange for that. The values are warm.
     */
    gray: {
      50: { value: '#FBF9F3' },
      // ANCHOR — the canvas
      100: { value: '#F7F4ED' },
      // ANCHOR — subtle fills
      200: { value: '#F0EEE6' },
      // ANCHOR — hairline borders
      300: { value: '#E5E3DA' },
      400: { value: '#D3D0C5' },
      500: { value: '#A9A69A' },
      600: { value: '#7E7C71' },
      700: { value: '#5A5952' },
      800: { value: '#3D3D38' },
      900: { value: '#262825' },
      950: { value: '#1A1C1A' },
    },

    /**
     * The only warm hue in the system, and the only one outside the green and
     * the neutral. Reserved for where Scout volunteers something the buyer did
     * not ask for — which is why `ambiguous` criteria wear it and nothing else
     * does. Spending it anywhere else costs it its meaning.
     */
    rust: {
      50: { value: '#FAF1EB' },
      100: { value: '#F1DCCF' },
      200: { value: '#E3BFA9' },
      300: { value: '#CE9878' },
      400: { value: '#B56B45' },
      // ANCHOR
      500: { value: '#9A4A28' },
      // ANCHOR
      600: { value: '#8E3F21' },
      700: { value: '#73331B' },
      800: { value: '#5A2815' },
      900: { value: '#431E10' },
    },
  },

  fonts: {
    /** The grotesk the application is made of: conversation body, navigation,
     *  buttons, labels, tables, metadata, forms. */
    body: { value: 'Geist, ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif' },
    heading: { value: 'Geist, ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif' },

    /**
     * The editorial serif, for display only: landing-page headlines,
     * conversation introductions, major narrative statements, and the
     * occasional high-level recommendation.
     *
     * It is NOT `heading` on purpose. Chakra's every `Heading` reads `heading`,
     * so a serif there would make the whole application serif and collapse the
     * distinction the two roles exist for. This one has to be opted into.
     */
    display: { value: 'Fraunces, Georgia, Times New Roman, serif' },
  },

  letterSpacings: {
    /** Display. Tight enough that it has to be deliberate — above roughly
     *  -0.035em a headline stops reading as editorial and starts reading as a
     *  large paragraph. */
    display: { value: '-0.045em' },
    /** Application headings. */
    heading: { value: '-0.025em' },
    /** UI: buttons, labels, navigation. */
    ui: { value: '-0.01em' },
  },

  lineHeights: {
    display: { value: '1' },
    heading: { value: '1.1' },
    body: { value: '1.5' },
  },

  sizes: {
    /** The fixed sidebar. */
    sidebar: { value: '240px' },
    /**
     * The conversation column and the detail panel are the same width — they
     * measured within two pixels of each other, which is not a coincidence.
     */
    thread: { value: '38rem' },
    panel: { value: '38rem' },
  },
})
